import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BookOpen, Search, X, ExternalLink } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { GLOSSARY_ENTRIES } from '@/content/glossary';
import { cn } from '@/lib/utils';

/**
 * Global searchable glossary panel.
 *
 * - Floating launcher button (bottom-right) opens a Sheet with the full
 *   glossary from `src/content/glossary.ts` — the same source used by
 *   the tooltip and Help page, so results always stay in sync.
 * - Filters by term, alias, or definition text; highlights matches.
 * - Keyboard shortcut: Cmd/Ctrl + G opens/closes the panel; Escape
 *   closes (native Sheet behaviour) and `/` inside focuses search.
 */

interface Props {
  /** Hide the floating launcher (e.g. on the Help page which already lists everything). */
  hideLauncher?: boolean;
}

function escapeRegex(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function Highlight({ text, query }: { text: string; query: string }) {
  if (!query.trim()) return <>{text}</>;
  const parts = text.split(new RegExp(`(${escapeRegex(query)})`, 'ig'));
  return (
    <>
      {parts.map((p, i) =>
        p.toLowerCase() === query.toLowerCase() ? (
          <mark key={i} className="bg-brand-magenta/40 text-white rounded-sm px-0.5">
            {p}
          </mark>
        ) : (
          <React.Fragment key={i}>{p}</React.Fragment>
        )
      )}
    </>
  );
}

const GlossaryPanel: React.FC<Props> = ({ hideLauncher }) => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // Auto-hide the launcher on routes that already surface the glossary.
  const suppressLauncher =
    hideLauncher ?? (location.pathname.startsWith('/help') || location.pathname.startsWith('/auth'));

  // Global keyboard shortcut: ⌘/Ctrl + G
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      if (meta && e.key.toLowerCase() === 'g') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return GLOSSARY_ENTRIES;
    return GLOSSARY_ENTRIES.filter((e) => {
      if (e.term.toLowerCase().includes(q)) return true;
      if (e.definition.toLowerCase().includes(q)) return true;
      if (e.example?.toLowerCase().includes(q)) return true;
      if (e.aliases?.some((a) => a.toLowerCase().includes(q))) return true;
      return false;
    });
  }, [query]);

  return (
    <>
      {!suppressLauncher && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open glossary (Cmd or Ctrl + G)"
          title="Glossary (⌘G)"
          className={cn(
            'fixed z-40 bottom-4 right-4 md:bottom-6 md:right-6 mb-safe',
            'inline-flex items-center justify-center gap-2 rounded-full',
            'px-3.5 py-2 text-sm font-medium min-h-[44px] min-w-[44px]',
            'bg-brand-magenta/90 hover:bg-brand-magenta text-white',
            'shadow-lg shadow-brand-magenta/25 backdrop-blur',
            'border border-white/15',
            'transition-transform hover:scale-[1.02] active:scale-[0.98]',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60'
          )}
        >
          <BookOpen className="h-4 w-4" />
          <span className="hidden sm:inline">Glossary</span>
        </button>
      )}

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-md bg-slate-950/95 text-slate-100 backdrop-blur border-l border-slate-800 p-0 flex flex-col"
        >
          <SheetHeader className="px-5 pt-5 pb-3 border-b border-slate-800">
            <SheetTitle className="flex items-center gap-2 text-lg text-slate-100">
              <BookOpen className="h-5 w-5 text-brand-magenta" />
              Glossary
              <span className="ml-auto text-xs font-normal text-slate-400">
                {filtered.length} / {GLOSSARY_ENTRIES.length}
              </span>
            </SheetTitle>
            <SheetDescription className="text-xs text-slate-300">
              Search LED video-wall terms used across the app. Press{' '}
              <kbd className="px-1 py-0.5 rounded bg-slate-800 text-slate-200 text-[10px] font-mono">⌘G</kbd>{' '}
              anywhere to open this panel.
            </SheetDescription>

            <div className="relative mt-2">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              <Input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search terms, aliases, or definitions…"
                className="pl-8 pr-8 bg-slate-900 border-slate-700 text-slate-100 placeholder:text-slate-500"
                aria-label="Search glossary"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-100"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-2 py-2">
            {filtered.length === 0 ? (
              <div className="text-sm text-slate-400 text-center py-10 px-4">
                No terms match{' '}
                <span className="text-slate-100 font-medium">"{query}"</span>.
                <br />
                Try a broader keyword, or{' '}
                <Link
                  to="/help#glossary"
                  onClick={() => setOpen(false)}
                  className="text-brand-magenta hover:underline"
                >
                  browse the full Help page
                </Link>
                .
              </div>
            ) : (
              <ul className="divide-y divide-slate-800">
                {filtered.map((entry) => (
                  <li key={entry.slug} className="px-3 py-3 hover:bg-slate-800/60 rounded-md">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-semibold text-sm text-slate-100">
                        <Highlight text={entry.term} query={query} />
                      </h3>
                      <Link
                        to={`/help#glossary-${entry.slug}`}
                        onClick={() => setOpen(false)}
                        aria-label={`Open ${entry.term} on Help page`}
                        className="shrink-0 text-slate-400 hover:text-brand-magenta transition-colors mt-0.5"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Link>
                    </div>
                    <p className="mt-1 text-xs leading-relaxed text-slate-300">
                      <Highlight text={entry.definition} query={query} />
                    </p>
                    {entry.example && (
                      <p className="mt-1.5 text-[11px] leading-relaxed text-slate-400 border-l-2 border-brand-magenta/50 pl-2 italic">
                        <span className="not-italic font-medium text-slate-200">Example: </span>
                        <Highlight text={entry.example} query={query} />
                      </p>
                    )}
                    {entry.aliases && entry.aliases.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {entry.aliases.slice(0, 4).map((a) => (
                          <span
                            key={a}
                            className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-800 text-slate-300"
                          >
                            {a}
                          </span>
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="border-t border-slate-800 px-5 py-3 flex items-center justify-between">
            <span className="text-[11px] text-slate-400">
              Missing a term?{' '}
              <Link
                to="/help#glossary"
                onClick={() => setOpen(false)}
                className="text-brand-magenta hover:underline"
              >
                Suggest it
              </Link>
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setOpen(false)}
              className="text-xs text-slate-200 hover:bg-slate-800 hover:text-slate-100"
            >
              Close
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default GlossaryPanel;
